import manifest from './swatch-thumbnails.json';

/**
 * FE-bundled swatch thumbnails. Companion to the backend-curated swatch data
 * (mirrors the `swatchExamples.ts` pattern). The image bytes live as static
 * assets under `public/swatches/{studioId}/{paletteId}/{swatchId}.png` (served
 * by Vite at the app origin — no auth, no signed-URL expiry), generated once
 * via fal.ai by `scripts/gen-swatch-thumbs.mjs`, which also maintains the
 * `swatch-thumbnails.json` manifest of what it produced.
 *
 * `Swatch.tsx` prefers a backend `swatch.thumbnailUrl` when present, then this
 * bundled URL, then the 2-char label tile. So seeding `thumbnailUrl` server-side
 * later transparently overrides these, and a swatch with no generated image
 * falls back cleanly (the manifest only lists ids that actually have a PNG, so
 * we never emit a 404 + broken-image flash).
 */

type Manifest = {
  [studioId: string]: { [paletteId: string]: readonly string[] } | unknown;
};

const data = manifest as unknown as Manifest;

/**
 * App-origin URL for a bundled swatch thumbnail, or `undefined` when no asset
 * is registered for the triple. The returned path is FE-origin (leading `/`)
 * and must NOT be passed through `resolveAssetUrl` (that targets the backend).
 */
export function swatchThumbnailUrl(
  studioId: string | undefined,
  paletteId: string,
  swatchId: string,
): string | undefined {
  if (!studioId || studioId === '_meta') return undefined;
  const studio = data[studioId];
  if (!studio || typeof studio !== 'object') return undefined;
  const ids = (studio as Record<string, readonly string[]>)[paletteId];
  if (!Array.isArray(ids) || !ids.includes(swatchId)) return undefined;
  return `/swatches/${studioId}/${paletteId}/${swatchId}.png`;
}
